import { Module } from 'vuex';
import {
  action,
  actionCreatorFactory,
  combineAction,
  combineMutation,
  mutation,
} from 'vuex-typescript-fsa/lib';
import { RootState } from '@/store';
import {
  CreateCvCommentRequest,
  CvComment,
  UpdateCvCommentRequest,
} from '@/../gen';

export const namespace = 'admin_cv_comment';
const actionCreator = actionCreatorFactory(namespace);

export const ToggleFetching = actionCreator<{ flag: boolean }>(
  'TOGGLE_FETCHING'
);
export const FetchCvComments = actionCreator<{ cvId: number }>(
  'FETCH_CV_COMMENTS'
);
export const GetCvComment = actionCreator<{
  cvId: number;
  id: number;
}>('GET_CV_COMMENT');
export const CreateCvComment = actionCreator<{
  cvId: number;
  data: CreateCvCommentRequest;
}>('CREATE_CV_COMMENT');
export const UpdateCvComment = actionCreator<{
  cvId: number;
  id: number;
  data: UpdateCvCommentRequest;
}>('UPDATE_CV_COMMENT');
export const DeleteCvComment = actionCreator<{
  cvId: number;
  id: number;
}>('DELETE_CV_COMMENT');
export const CvCommentsFetched = actionCreator<CvComment[]>(
  'CV_COMMENTS_FETCHED'
);
export const CvCommentUpdated = actionCreator<CvComment>('CV_COMMENT_UPDATED');
export const CvCommentDeleted = actionCreator<{ cvCommentId: number }>(
  'CV_COMMENT_DELETED'
);
export const ResetState = actionCreator<null>('RESET_STATE');

export interface State {
  isFetching: boolean;
  cvCommentMap: { [id: number]: CvComment };
}

const initialState = (): State => {
  return { isFetching: true, cvCommentMap: {} };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: initialState,
  actions: combineAction(
    action(ToggleFetching, function(context, action) {
      context.commit(ToggleFetching(action.payload));
    }),
    action(FetchCvComments, async function(context, action) {
      const { cvId } = action.payload;
      const { data } = await this.proffAdminApi.listCvComments(cvId);
      context.commit(CvCommentsFetched(data.data.map(c => c.attributes)));
    }),
    action(CreateCvComment, async function(context, action) {
      const { cvId, data } = action.payload;
      const result = await this.proffAdminApi.createCvComment(cvId, data);
      const cvComment = result.data.data.attributes;
      context.commit(CvCommentUpdated(cvComment));
      return cvComment;
    }),
    action(UpdateCvComment, async function(context, action) {
      const { cvId, id, data } = action.payload;
      const result = await this.proffAdminApi.updateCvComment(cvId, id, data);
      context.commit(CvCommentUpdated(result.data.data.attributes));
    }),
    action(DeleteCvComment, async function(context, action) {
      const { cvId, id } = action.payload;
      await this.proffAdminApi.deleteCvComment(cvId, id);
      context.commit(CvCommentDeleted({ cvCommentId: id }));
    }),
    action(ResetState, function(context, action) {
      context.commit(ResetState(action.payload));
    })
  ),
  mutations: combineMutation(
    mutation(ToggleFetching, (state, action) => {
      state.isFetching = action.payload.flag;
    }),
    mutation(CvCommentsFetched, (state, action) => {
      const cvComments = action.payload;
      state.cvCommentMap = cvComments.reduce((r, cvComment) => {
        r[cvComment.id] = Object.assign({}, cvComment);
        return r;
      }, {});
    }),
    mutation(CvCommentUpdated, (state, action) => {
      const cvComment = action.payload;
      state.cvCommentMap = Object.assign({}, state.cvCommentMap, {
        [cvComment.id]: cvComment,
      });
    }),
    mutation(CvCommentDeleted, (state, action) => {
      const deletedMap = Object.assign({}, state.cvCommentMap);
      delete deletedMap[action.payload.cvCommentId];
      state.cvCommentMap = deletedMap;
    }),
    mutation(ResetState, state => {
      Object.assign(state, initialState());
    })
  ),
  getters: {
    isFetching: state => state.isFetching,
    findCvCommentById: state => (cvCommentId: number) =>
      state.cvCommentMap[cvCommentId] || null,
  },
};
